import { useEffect, useRef } from 'react'
import type { ClipboardEvent, JSX, KeyboardEvent } from 'react'

/** Mirrors `CODE_LENGTH` in `app/security/otp.py`. */
export const CODE_LENGTH = 6

/**
 * Six boxes for a six-digit SMS code (S-D2 screen 05).
 *
 * Six boxes rather than one field because people read an SMS code in pairs, and
 * the boxes are what make a mistyped digit visible before the code is spent —
 * five wrong guesses burn it (`app/security/otp.py`).
 *
 * Three behaviours it would be wrong to leave out, each of which is how people
 * actually enter these:
 *
 *  * **Paste fills every box.** Android and iOS both offer the code from the
 *    notification as a single paste, and a paste that landed six digits in box
 *    one would be the commonest way to fail.
 *  * **Backspace on an empty box goes back one.** Otherwise correcting the last
 *    digit means reaching for the mouse.
 *  * **Typing advances, arrows move.** `autoComplete="one-time-code"` is on the
 *    first box, which is what lets Safari and Chrome offer the code from the
 *    message without the user opening it.
 *
 * The value is the parent's — the boxes hold no state of their own, so a wrong
 * code the server rejected can be cleared from one place.
 */
export function OtpInput({
  value,
  onChange,
  onComplete,
  invalid = false,
  disabled = false,
}: {
  value: string
  onChange: (next: string) => void
  /** Called when the sixth digit lands, so submitting needs no extra tap. */
  onComplete?: (code: string) => void
  invalid?: boolean
  disabled?: boolean
}): JSX.Element {
  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value[index] ?? '')

  // Held in a ref so a parent may pass an inline closure without this firing
  // again on every render.
  const complete = useRef(onComplete)
  complete.current = onComplete

  useEffect(() => {
    if (value.length === CODE_LENGTH) complete.current?.(value)
  }, [value])

  function focus(index: number): void {
    boxes.current[Math.min(Math.max(index, 0), CODE_LENGTH - 1)]?.focus()
  }

  function setDigit(index: number, typed: string): void {
    const digit = typed.replace(/\D/g, '').slice(-1)
    const next = digits.slice()
    next[index] = digit
    onChange(next.join('').slice(0, CODE_LENGTH))
    if (digit) focus(index + 1)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>, index: number): void {
    if (event.key === 'Backspace' && !digits[index]) {
      event.preventDefault()
      const next = digits.slice()
      next[index - 1] = ''
      onChange(next.join(''))
      focus(index - 1)
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focus(index - 1)
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focus(index + 1)
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) return
    event.preventDefault()
    onChange(pasted)
    focus(pasted.length)
  }

  return (
    <div
      className="app-otp"
      role="group"
      aria-label={`${String(CODE_LENGTH)}-digit code`}
      data-state={invalid ? 'invalid' : undefined}
    >
      {digits.map((digit, index) => (
        <input
          // Position is the identity here: the boxes never reorder, and keying
          // on the digit would remount a box the moment its value changed.
          key={`digit-${String(index)}`}
          ref={(element) => {
            boxes.current[index] = element
          }}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-label={`Digit ${String(index + 1)}`}
          value={digit}
          onChange={(event) => {
            setDigit(index, event.target.value)
          }}
          onKeyDown={(event) => {
            onKeyDown(event, index)
          }}
          onPaste={onPaste}
          autoFocus={index === 0}
        />
      ))}
    </div>
  )
}
